import { Link } from "react-router-dom";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { MediaTile } from "@/components/admin/ReviewMedia";
import { selfieUrl } from "@/lib/api";
import { REPORT_REASONS } from "@/lib/constants";
import { formatDate, humanize } from "@/lib/format";

function ReportList({ title, items, other, testId }) {
  return (
    <Panel className="p-6" data-testid={testId}>
      <h2 className="mb-3 font-semibold">{title} ({items?.length || 0})</h2>
      {items?.length ? (
        <ul className="divide-y divide-border">
          {items.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-3 text-sm" data-testid={`${testId}-row-${r.id}`}>
              <span className="min-w-0 flex-1">{REPORT_REASONS[r.reason] || humanize(r.reason)}
                {r[other] && <Link to={`/admin/users/${r[other]}`} className="cw-muted block font-mono text-xs hover:underline">{other === "reporter_id" ? "by " : "about "}{r[other]}</Link>}</span>
              <Pill tone={r.status === "open" ? "warning" : "neutral"} className="capitalize">{r.status}</Pill>
              {r.resolution && <Pill className="capitalize">{humanize(r.resolution)}</Pill>}
              <span className="cw-muted w-36 text-right text-xs">{formatDate(r.created_at)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="cw-muted text-sm">None.</p>}
    </Panel>
  );
}

export function ReportsTab({ data }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ReportList title="Reports about them" items={data.reports_received} other="reporter_id" testId="user-reports-received" />
      <ReportList title="Reports they made" items={data.reports_made} other="reported_id" testId="user-reports-made" />
    </div>
  );
}

const TONE = { approved: "success", rejected: "error", pending: "brand", reopened: "warning" };
const KIND = { onboarding: "Onboarding selfie", retake: "Retake selfie", phone_change: "Phone change", retake_reopened: "Retake requested by an admin" };

function HistoryRow({ r, showSelfie }) {
  return (
    <li className="flex flex-wrap items-start gap-4 py-4 text-sm" data-testid={`user-verification-row-${r.id}`}>
      {showSelfie && r.selfie_id && <MediaTile src={selfieUrl(`/api/admin/reviews/${r.id}/selfie`)} alt={`${KIND[r.kind] || r.kind} selfie`} className="w-20 shrink-0" testId={`user-verification-selfie-${r.id}`} />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium" data-testid={`user-verification-kind-${r.id}`}>{KIND[r.kind] || humanize(r.kind)}</p>
          <Pill tone={TONE[r.status] || "neutral"} className="capitalize" data-testid={`user-verification-status-${r.id}`}>{r.status}</Pill>
        </div>
        <p className="cw-muted mt-1 text-xs">Submitted {formatDate(r.submitted_at)}{r.reviewed_at && r.kind !== "retake_reopened" ? ` · reviewed ${formatDate(r.reviewed_at)}` : ""}{r.reviewed_by ? ` · ${r.reviewed_by}` : ""}</p>
        {r.note && <p className="cw-soft mt-1 text-xs" data-testid={`user-verification-note-${r.id}`}>“{r.note}”</p>}
      </div>
    </li>
  );
}

export function VerificationTab({ data, role }) {
  const v = data.verification || {};
  const history = data.verification_history || [];
  return (
    <div className="space-y-6">
      <Panel className="p-6" data-testid="user-verification-current">
        <h2 className="font-semibold">Current</h2>
        <p className="mt-2 text-sm">Status: <Pill tone={v.status === "verified" ? "success" : "neutral"} className="capitalize" data-testid="user-verification-status">{humanize(v.status || "unverified")}</Pill></p>
      </Panel>
      <Panel className="p-6" data-testid="user-verification-history">
        <h2 className="font-semibold">History</h2>
        <p className="cw-soft mt-1 text-sm">Newest first.</p>
        {history.length ? <ul className="mt-2 divide-y divide-border">{history.map((r) => <HistoryRow key={r.id} r={r} showSelfie={role !== "support"} />)}</ul>
          : <p className="cw-muted mt-3 text-sm" data-testid="user-verification-history-empty">No verification history yet.</p>}
      </Panel>
    </div>
  );
}

export function DevicesTab({ data }) {
  const d = data.devices || {};
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel className="p-6" data-testid="user-devices">
        <h2 className="mb-3 font-semibold">Device IDs ({d.device_ids?.length || 0})</h2>
        {d.device_ids?.length ? <ul className="space-y-1.5 font-mono text-xs">{d.device_ids.map((x) => <li key={x} className="break-all">{x}</li>)}</ul> : <p className="cw-muted text-sm">None.</p>}
      </Panel>
      <Panel className="p-6" data-testid="user-push-tokens">
        <h2 className="mb-3 font-semibold">Push tokens ({data.push_tokens_count ?? d.push_tokens?.length ?? 0})</h2>
        {d.push_tokens?.length ? <ul className="divide-y divide-border text-sm">{d.push_tokens.map((t, i) => (
          <li key={i} className="flex justify-between py-2"><span className="capitalize">{t.platform || "unknown"}</span><span className="cw-muted text-xs">last seen {formatDate(t.last_seen)}</span></li>
        ))}</ul> : <p className="cw-muted text-sm">No push tokens. Pushes to them will fail with no_device_token.</p>}
      </Panel>
    </div>
  );
}
